import { Fragment, useEffect, useState } from 'react';
import { api } from '../api/client';
import { Badge, Bandeau, Carte, Kpi, Squelette } from '../components/ui';
import { montant, montantCourt, roleLabel } from '../lib/format';
import type { BilanVehicules } from '../api/types';

interface Flotte { tauxUtilisation: number; revPACMinor: number; prixMoyenJourMinor: number; dureeMoyenneJours: number; chiffreAffairesMinor: number; nbLocations: number; diagnostic: string }
interface Rentab { vehicules: { vehicleId: string; libelle: string; immatriculation: string; joursLoues: number; tauxUtilisation: number; chiffreAffairesMinor: number; coutEntretienMinor: number; margeNetteMinor: number; verdict: string }[] }
interface Refus { nombre: number; valeurPerdueMinor: number; parCategorie: { categorie: string; nombre: number; valeurMinor: number }[] }
interface Equipe { employes: { userId: string; nom: string; role: string; contratsCrees: number; reservationsCreees: number; caEncaisseMinor: number; etatsDesLieuxSaisis: number; dommagesNonConstates: number; coutDommagesNonConstatesMinor: number; demandesCorrection: number }[] }

const VERDICT: Record<string, { s: string; l: string }> = {
  a_garder: { s: 'en_cours', l: 'A garder' },
  a_surveiller: { s: 'retour', l: 'A surveiller' },
  a_vendre: { s: 'retard', l: 'A vendre' },
};

/** « août 26 » plutot que « 2026-08 » : on lit un tableau, pas une base. */
const libelleCourt = (mois: string) => {
  const [a, m] = mois.split('-').map(Number);
  return new Date(a!, (m ?? 1) - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

/** Une case se lit d'un coup d'oeil : plus c'est plein, plus c'est vert. */
function fondTaux(taux: number): string | undefined {
  if (taux <= 0) return undefined;
  if (taux >= 75) return 'var(--succes-doux)';
  if (taux >= 45) return 'var(--accent-doux)';
  return 'var(--danger-doux)';
}

export default function Analytique() {
  const [f, setF] = useState<Flotte | null>(null);
  const [r, setR] = useState<Rentab | null>(null);
  const [ref, setRef] = useState<Refus | null>(null);
  const [eq, setEq] = useState<Equipe | null>(null);
  const [bilan, setBilan] = useState<BilanVehicules | null>(null);
  const [nbMois, setNbMois] = useState(6);
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    api.get<Flotte>('/analytique/flotte').then(setF);
    api.get<Rentab>('/analytique/rentabilite').then(setR);
    api.get<Refus>('/analytique/demandes-refusees').then(setRef);
    api.get<BilanVehicules>(`/analytique/bilan-vehicules?nb=${nbMois}`).then(setBilan).catch(() => {});
    api.get<Equipe>('/analytique/equipe').then(setEq);
  }, [nbMois]);

  if (!f) return <Squelette lignes={8} />;

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>Analyses</h1><p>Mois en cours · réservé aux gérants</p></div>
        <div className="actions">
          <select className="btn btn-s" value={nbMois} onChange={(e) => { setNbMois(Number(e.target.value)); setBilan(null); }}
                  style={{ paddingRight: 8 }}>
            <option value={3}>3 mois</option><option value={6}>6 mois</option>
            <option value={12}>12 mois</option>
          </select>
        </div>
      </div>

      <div className="grille g4">
        <Kpi libelle="Taux d'utilisation" valeur={`${f.tauxUtilisation} %`} detail="cible du métier 75–85 %" />
        <Kpi libelle="Revenu par voiture / jour" valeur={montantCourt(f.revPACMinor)} detail="RevPAC" />
        <Kpi libelle="Prix moyen journalier" valeur={montantCourt(f.prixMoyenJourMinor)} detail="réellement pratiqué" />
        <Kpi libelle="Durée moyenne" valeur={`${f.dureeMoyenneJours} j`} detail={`${f.nbLocations} location(s)`} />
      </div>

      <div style={{ marginTop: 14 }}><Bandeau type="info" titre="Diagnostic">{f.diagnostic}</Bandeau></div>

      {ref && ref.nombre > 0 && (
        <div style={{ marginTop: 16 }}>
          <Carte titre="L'argent que vous ne voyez pas" className="signature-kpi">
            <div className="kpi">
              <div className="v">{montant(ref.valeurPerdueMinor)}</div>
              <div className="d">{ref.nombre} demande(s) refusée(s) faute de véhicule disponible sur la période</div>
            </div>
            <table className="tbl compact" style={{ marginTop: 12 }}>
              <thead><tr><th>Catégorie la plus demandée</th><th className="droite">Refus</th><th className="droite">Manque a gagner</th></tr></thead>
              <tbody>
                {ref.parCategorie.map((c) => (
                  <tr key={c.categorie}><td>{c.categorie}</td><td className="droite num">{c.nombre}</td><td className="droite montant">{montant(c.valeurMinor)}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="mini sourdine" style={{ marginTop: 10 }}>
              Chaque recherche de disponibilité restée sans résultat est enregistrée et valorisée au tarif moyen de la flotte.
              C'est ce qui vous dira quand acheter le véhicule suivant.
            </p>
          </Carte>
        </div>
      )}

      {/*
        * Le bilan par voiture ET par mois.
        *
        * Le taux global d'une flotte melange la Clio qui tourne toute l'annee et
        * le monospace qui ne sort qu'en ete : il ne dit rien d'utile. Ici on voit
        * qu'une voiture ne travaille que trois mois sur douze — et on decide.
        */}
      {bilan && (
        <div style={{ marginTop: 16 }}>
          <Carte pad={false} titre="Par voiture et par mois">
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th>Véhicule</th>
                    {bilan.mois.map((m) => <th key={m} className="droite">{libelleCourt(m)}</th>)}
                    <th className="droite">Total</th>
                    <th className="droite">Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {bilan.vehicules.map((v) => (
                    <Fragment key={v.vehicleId}>
                      <tr onClick={() => setDetail(detail === v.vehicleId ? null : v.vehicleId)} style={{ cursor: 'pointer' }}>
                        <td>
                          <b>{v.libelle}</b>
                          <div className="mini sourdine">{v.immatriculation} · {v.categorie}</div>
                        </td>
                        {v.mois.map((m) => (
                          <td key={m.mois} className="droite num"
                              title={`${m.joursLoues} jour(s) loué(s) · ${montant(m.caTotalMinor)}`}
                              style={{ background: fondTaux(m.tauxUtilisation) }}>
                            {m.joursDisponibles === 0 ? '—' : `${Math.round(m.tauxUtilisation)} %`}
                          </td>
                        ))}
                        <td className="droite montant">{montantCourt(v.total.caTotalMinor)}</td>
                        <td className="droite montant" style={{ color: v.total.margeMinor < 0 ? 'var(--danger)' : undefined }}>
                          {montantCourt(v.total.margeMinor)}
                        </td>
                      </tr>
                      {detail === v.vehicleId && (
                        <tr>
                          <td colSpan={bilan.mois.length + 3} style={{ background: 'var(--zebre)', padding: 12 }}>
                            <table className="tbl compact">
                              <thead>
                                <tr>
                                  <th>Mois</th><th className="droite">Locations</th><th className="droite">Jours loués</th>
                                  <th className="droite">CA location</th><th className="droite">Suppléments</th>
                                  <th className="droite">Prix moyen / jour</th>
                                  <th className="droite">Entretien</th><th className="droite">Jours au garage</th>
                                  <th className="droite">Marge</th>
                                </tr>
                              </thead>
                              <tbody>
                                {v.mois.map((m) => (
                                  <tr key={m.mois}>
                                    <td>{libelleCourt(m.mois)}</td>
                                    <td className="droite num">{m.nbLocations || '—'}</td>
                                    <td className="droite num">{m.joursLoues || '—'} / {m.joursDisponibles}</td>
                                    <td className="droite montant">{m.caLocationMinor ? montant(m.caLocationMinor) : '—'}</td>
                                    <td className="droite montant">{m.caSupplementsMinor ? montant(m.caSupplementsMinor) : '—'}</td>
                                    <td className="droite montant">{m.prixMoyenJourMinor ? montant(m.prixMoyenJourMinor) : '—'}</td>
                                    <td className="droite montant" style={m.coutEntretienMinor ? { color: 'var(--danger)' } : undefined}>
                                      {m.coutEntretienMinor ? montant(m.coutEntretienMinor) : '—'}
                                    </td>
                                    <td className="droite num">{m.joursImmobilise || '—'}</td>
                                    <td className="droite montant">{montant(m.margeMinor)}</td>
                                  </tr>
                                ))}
                                <tr style={{ borderTop: '2px solid var(--bord-fort)' }}>
                                  <td><b>Total</b></td>
                                  <td className="droite num"><b>{v.total.nbLocations}</b></td>
                                  <td className="droite num"><b>{v.total.joursLoues} / {v.total.joursDisponibles}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.caLocationMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.caSupplementsMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.prixMoyenJourMinor)}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.coutEntretienMinor)}</b></td>
                                  <td className="droite num"><b>{v.total.joursImmobilise}</b></td>
                                  <td className="droite montant"><b>{montant(v.total.margeMinor)}</b></td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
          <p className="mini sourdine" style={{ marginTop: 12 }}>
            Chaque case est le taux d'occupation du mois. <b>Cliquez une ligne</b> pour le détail :
            locations, jours loués, prix moyen réellement pratiqué, entretien et jours au garage.
            Une location à cheval sur deux mois est répartie au prorata des jours.
          </p>
        </div>
      )}

      {r && (
        <div style={{ marginTop: 16 }}>
          <Carte titre="Rentabilité, véhicule par véhicule" pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>Véhicule</th><th>Immatriculation</th><th className="droite">Jours loués</th><th className="droite">Utilisation</th>
                      <th className="droite">CA</th><th className="droite">Entretien</th><th className="droite">Marge nette</th><th>Verdict</th></tr>
                </thead>
                <tbody>
                  {r.vehicules.map((v) => (
                    <tr key={v.vehicleId}>
                      <td><b>{v.libelle}</b></td>
                      <td className="num mini sourdine">{v.immatriculation}</td>
                      <td className="droite num">{v.joursLoues}</td>
                      <td className="droite num">{v.tauxUtilisation} %</td>
                      <td className="droite montant">{montantCourt(v.chiffreAffairesMinor)}</td>
                      <td className="droite montant sourdine">{montantCourt(v.coutEntretienMinor)}</td>
                      <td className="droite montant" style={{ color: v.margeNetteMinor < 0 ? 'var(--danger)' : undefined }}>{montantCourt(v.margeNetteMinor)}</td>
                      <td><Badge statut={VERDICT[v.verdict]?.s ?? 'terminee'} libelle={VERDICT[v.verdict]?.l ?? v.verdict} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>
      )}

      {eq && (
        <div style={{ marginTop: 16 }}>
          <Carte titre="Activité de l'équipe" pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>Personne</th><th className="droite">Réservations</th><th className="droite">Contrats</th>
                      <th className="droite">Encaissé</th><th className="droite">États des lieux</th>
                      <th className="droite">Dommages non constatés</th><th className="droite">Demandes de correction</th></tr>
                </thead>
                <tbody>
                  {eq.employes.map((e) => (
                    <tr key={e.userId}>
                      <td><b>{e.nom}</b> <span className="mini sourdine">{roleLabel(e.role)}</span></td>
                      <td className="droite num">{e.reservationsCreees}</td>
                      <td className="droite num">{e.contratsCrees}</td>
                      <td className="droite montant">{montantCourt(e.caEncaisseMinor)}</td>
                      <td className="droite num">{e.etatsDesLieuxSaisis}</td>
                      <td className="droite num" style={{ background: e.dommagesNonConstates > 0 ? 'var(--danger-doux)' : undefined, color: e.dommagesNonConstates > 0 ? 'var(--danger)' : undefined }}
                          title="Dommages découverts au retour mais absents du constat de départ — chacun est une perte sèche">
                        {e.dommagesNonConstates}{e.coutDommagesNonConstatesMinor > 0 && ` · ${montantCourt(e.coutDommagesNonConstatesMinor)}`}
                      </td>
                      <td className="droite num">{e.demandesCorrection}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="carte-p">
              <p className="mini sourdine">
                Ces indicateurs mesurent le travail produit. Le logiciel n'enregistre ni position, ni temps de présence,
                ni activité hors de l'application.
              </p>
            </div>
          </Carte>
        </div>
      )}
    </>
  );
}
